/**
 * Follows each Turbo form submission from its request to the moment its result is on the page.
 *
 * Turbo ends a submission (`turbo:submit-end`) as soon as the response headers arrive, before the
 * body is read and before anything renders. What the person waits for comes later, and which event
 * ends it depends on the response:
 *
 * - `request`: the form request is in flight.
 * - `visit`: a success body is being read, after which Turbo proposes the follow-up visit.
 * - `visiting`: that visit has started and ends when its page renders.
 * - `render`: a refused body is being read, after which Turbo renders it in place.
 * - `frame`: a `turbo-frame` submission's body is being read, after which the frame renders it. A
 *   response without the frame opens as a full page (`turbo-frame-missing.ts`) and continues as `visit`.
 * - `stream`: a Turbo Stream response's actions are being applied. A live update that renders meanwhile is not one of them.
 *
 * A submission is released when its result is on the page, or when Turbo will neither visit nor render
 * what came back: a failed request, a response that is not a page, one a listener took over, an empty or
 * unreadable body, or a follow-up visit a listener cancelled. Nothing is released by a timer.
 *
 * `scope` picks the submissions followed. `full-page` follows only full-page submits and releases a Turbo
 * Stream response at `turbo:submit-end`. `every-update` also follows `turbo-frame` submits and holds a Turbo
 * Stream response until its own actions have run.
 */

import {
   frameIdOf,
   hasEmptyBody,
   isFrameSubmission,
   isPreviewRender,
   isStreamResponse,
   recordFetchResponse,
   streamActionMarkups,
   turboReadsBody,
   type FormSubmissionLike,
   type SubmissionResponse,
   type SubmitDetail,
} from "./turbo-submit-event";

/** A `turbo-stream` action that rendered while a submission waited on its response; `finished` once it has run, whether it succeeded or threw. */
interface RenderedAction {
   readonly markup: string;
   finished: boolean;
}

/** `request`: the form request is in flight, so there is no response yet. */
interface RequestFollowed {
   readonly phase: "request";
}

/** `visit`, `visiting`, `render` and `frame`: Turbo has a response it reads or renders as a page. */
interface ResponseFollowed<P extends "visit" | "visiting" | "render" | "frame"> {
   readonly phase: P;
   readonly fetchResponse: SubmissionResponse;
}

interface StreamFollowed {
   readonly phase: "stream";
   /** The markup of the response's own actions that have not run yet; undefined until its body is read. */
   ownActions: string[] | undefined;
   /** Every action rendered since `submit-end`, the response's own and live updates alike, until `ownActions` is known to sort them. */
   rendered: RenderedAction[];
}

type Followed =
   | RequestFollowed
   | ResponseFollowed<"visit">
   | ResponseFollowed<"visiting">
   | ResponseFollowed<"render">
   | ResponseFollowed<"frame">
   | StreamFollowed;

type Phase = Followed["phase"];

export interface SubmissionPhaseOptions {
   readonly scope: "full-page" | "every-update";
   /** Called at `turbo:submit-end` for a followed submission, before it is sorted into a phase. */
   readonly onSubmitEnd?: (submission: FormSubmissionLike) => void;
   /** Called once for a followed submission, when its result is on the page or Turbo will show none. */
   readonly onRelease?: (submission: FormSubmissionLike) => void;
}

export interface SubmissionPhases {
   /** True while any followed submission is not yet released. */
   readonly following: boolean;
   follows(submission: FormSubmissionLike): boolean;
   releaseAll(): void;
   /** Removes the listeners and releases every followed submission. */
   uninstall(): void;
}

type StreamRenderDetail = { newStream?: Element; render?: (streamElement: Element) => void | Promise<void> };

export function installSubmissionPhases(options: SubmissionPhaseOptions): SubmissionPhases {
   const followed = new Map<FormSubmissionLike, Followed>();
   const followsEveryUpdate = options.scope === "every-update";

   function release(submission: FormSubmissionLike): void {
      if (followed.delete(submission)) {
         options.onRelease?.(submission);
      }
   }

   function releaseAll(): void {
      for (const submission of [...followed.keys()]) {
         release(submission);
      }
   }

   function inPhase<P extends Phase>(...phases: P[]): [FormSubmissionLike, Extract<Followed, { phase: P }>][] {
      return [...followed].filter(([, entry]) => phases.includes(entry.phase as P)) as [FormSubmissionLike, Extract<Followed, { phase: P }>][];
   }

   function onBeforeFetchResponse(event: Event): void {
      recordFetchResponse(event);
   }

   function onSubmitStart(event: Event): void {
      const submission = (event as CustomEvent<SubmitDetail>).detail?.formSubmission;
      if (!submission || (!followsEveryUpdate && isFrameSubmission(submission))) {
         return;
      }
      followed.set(submission, { phase: "request" });
   }

   function settleStream(submission: FormSubmissionLike, entry: StreamFollowed): void {
      if (!entry.ownActions) {
         return;
      }
      const finished = entry.rendered.filter((action) => action.finished);
      entry.rendered = entry.rendered.filter((action) => !action.finished);
      for (const { markup } of finished) {
         const index = entry.ownActions.indexOf(markup);
         if (index !== -1) {
            entry.ownActions.splice(index, 1);
         }
      }
      if (entry.ownActions.length === 0) {
         release(submission);
      }
   }

   function followStream(submission: FormSubmissionLike, fetchResponse: SubmissionResponse): void {
      const entry: StreamFollowed = { phase: "stream", ownActions: undefined, rendered: [] };
      followed.set(submission, entry);
      void fetchResponse.responseHTML
         .catch(() => undefined)
         .then((html) => {
            entry.ownActions = streamActionMarkups(html);
            if (followed.get(submission) === entry) {
               settleStream(submission, entry);
            }
         });
   }

   function onSubmitEnd(event: Event): void {
      const { formSubmission, fetchResponse } = (event as CustomEvent<SubmitDetail>).detail ?? {};
      const entry = formSubmission && followed.get(formSubmission);
      if (!formSubmission || !entry) {
         return;
      }
      options.onSubmitEnd?.(formSubmission);

      if (!fetchResponse) {
         release(formSubmission);
         return;
      }

      if (followsEveryUpdate && isStreamResponse(fetchResponse)) {
         followStream(formSubmission, fetchResponse);
         return;
      }

      if (!turboReadsBody(fetchResponse)) {
         release(formSubmission);
         return;
      }

      if (isFrameSubmission(formSubmission)) {
         followed.set(formSubmission, { phase: "frame", fetchResponse });
      } else {
         followed.set(formSubmission, { phase: fetchResponse.succeeded ? "visit" : "render", fetchResponse });
      }
      // Turbo neither visits nor renders an empty or unreadable body.
      void hasEmptyBody(fetchResponse).then((empty) => {
         if (empty && followed.has(formSubmission)) {
            release(formSubmission);
         }
      });
   }

   function onBeforeVisit(event: Event): void {
      // Only the follow-up visit's cancellation ends a submission; any other cancelled visit does not.
      const visitPhase = inPhase("visit");
      if (visitPhase.length === 0) {
         return;
      }
      // Read after every listener has run; Turbo dispatches synchronously.
      queueMicrotask(() => {
         if (!event.defaultPrevented) {
            return;
         }
         for (const [submission] of visitPhase) {
            if (followed.get(submission)?.phase === "visit") {
               release(submission);
            }
         }
      });
   }

   function onVisit(): void {
      for (const [submission, entry] of inPhase("visit")) {
         followed.set(submission, { phase: "visiting", fetchResponse: entry.fetchResponse });
      }
   }

   function onRender(): void {
      if (isPreviewRender()) {
         return;
      }
      for (const [submission] of inPhase("render", "visiting")) {
         release(submission);
      }
   }

   function onFrameRender(event: Event): void {
      const fetchResponse = (event as CustomEvent<{ fetchResponse?: SubmissionResponse }>).detail?.fetchResponse;
      for (const [submission, entry] of inPhase("frame")) {
         if (entry.fetchResponse === fetchResponse) {
            release(submission);
         }
      }
   }

   function onFrameMissing(event: Event): void {
      const frameId = (event.target as Element | null)?.id;
      for (const [submission, entry] of inPhase("frame")) {
         if (frameIdOf(submission) === frameId) {
            followed.set(submission, { phase: "visit", fetchResponse: entry.fetchResponse });
         }
      }
   }

   function onBeforeStreamRender(event: Event): void {
      const detail = (event as CustomEvent<StreamRenderDetail>).detail;
      const render = detail?.render;
      const markup = detail?.newStream?.outerHTML;
      if (typeof render !== "function" || markup === undefined) {
         return;
      }
      // A live update (a channel's `turbo:before-stream-render`) shares this event with the response's own
      // actions, so the event cannot tell them apart. The response body can: its actions are matched by markup,
      // once the body is read, against everything that rendered meanwhile. `newStream` is the element Turbo
      // renders from the response (`StreamMessage` imports the parsed element and connecting it dispatches this event).
      const waiting = inPhase("stream").map(([submission, entry]) => {
         const action: RenderedAction = { markup, finished: false };
         entry.rendered.push(action);
         return { submission, entry, action };
      });
      detail.render = async (streamElement: Element) => {
         try {
            await render(streamElement);
         } finally {
            for (const { submission, entry, action } of waiting) {
               action.finished = true;
               if (followed.get(submission) === entry) {
                  settleStream(submission, entry);
               }
            }
         }
      };
   }

   document.addEventListener("turbo:submit-start", onSubmitStart);
   document.addEventListener("turbo:before-fetch-response", onBeforeFetchResponse);
   document.addEventListener("turbo:submit-end", onSubmitEnd);
   document.addEventListener("turbo:before-visit", onBeforeVisit);
   document.addEventListener("turbo:visit", onVisit);
   document.addEventListener("turbo:render", onRender);
   if (followsEveryUpdate) {
      document.addEventListener("turbo:frame-render", onFrameRender);
      document.addEventListener("turbo:frame-missing", onFrameMissing);
      document.addEventListener("turbo:before-stream-render", onBeforeStreamRender);
   }

   return {
      get following() {
         return followed.size > 0;
      },
      follows: (submission) => followed.has(submission),
      releaseAll,
      uninstall() {
         document.removeEventListener("turbo:submit-start", onSubmitStart);
         document.removeEventListener("turbo:before-fetch-response", onBeforeFetchResponse);
         document.removeEventListener("turbo:submit-end", onSubmitEnd);
         document.removeEventListener("turbo:before-visit", onBeforeVisit);
         document.removeEventListener("turbo:visit", onVisit);
         document.removeEventListener("turbo:render", onRender);
         document.removeEventListener("turbo:frame-render", onFrameRender);
         document.removeEventListener("turbo:frame-missing", onFrameMissing);
         document.removeEventListener("turbo:before-stream-render", onBeforeStreamRender);
         releaseAll();
      },
   };
}
